import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { buildImageObj } from '../../lib/helpers';
import { imageUrlFor } from '../../lib/image-url';
import { Box, Layer, Image, Grid, Text, Stack, Keyboard } from 'grommet';
import { Close } from 'grommet-icons';

/**
 * Intermal component: A thumbnail for one image in the gallery.
 */
const Thumbnail = ({ image }) => {
  const imageUrl = imageUrlFor(buildImageObj(image))
    .width(300)
    .height(Math.floor((9 / 16) * 300))
    .fit('crop')
    .auto('format')
    .url();

  return <Image fit="cover" src={imageUrl} />;
};

/**
 * Internal component: the stacked large image, caption and close button.
 */
const ImageWithCaption = ({ image, closeHandler }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  const imageUrl = imageUrlFor(buildImageObj(image))
    .width(1800)
    .height(Math.floor((9 / 16) * 1800))
    .fit('crop')
    .auto('format')
    .url();

  return (
    <Box flex={true} justify="center">
      <Stack anchor="top-right">
        <Box width="xlarge">
          <Image
            fit="cover"
            fill={true}
            src={imageUrl}
            style={!imageLoaded ? { display: 'none' } : null}
            onLoad={() => setImageLoaded(true)}
          />
          {imageLoaded && (
            <Box pad="medium">
              <Text>{image.caption}</Text>
            </Box>
          )}
        </Box>
        {imageLoaded && (
          <Box
            margin="large"
            background={{ color: 'dark-1' }}
            opacity="medium"
            pad="small"
          >
            <Close size="large" onClick={closeHandler} cursor="pointer" />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Exported component: the image gallery.
 */
const ImageGallery = ({ images }) => {
  const [activeImageId, setActiveImageId] = useState(null);

  /**
   * Determines whether the current image is active.
   *
   * @param object image
   *   A Sanity image object.
   *
   * @returns boolean
   *   Whether the current image is active.
   */
  const imageIsActive = (image) => {
    return activeImageId === image.asset._id && activeImageId !== null;
  };

  /**
   * Moves to the next or previous image.
   *
   * @param String direction
   *   Either "next" or "prev"
   * @param object currentImage
   *   The current Sanity image object that we are navigating from.
   */
  const navigate = (direction, currentImage) => {
    // Find next image
    const currentIndex = images.findIndex(
      (image) => image.asset._id === currentImage.asset._id
    );

    const targetImage =
      images[direction === 'next' ? currentIndex + 1 : currentIndex - 1];

    if (targetImage) {
      setActiveImageId(targetImage.asset._id);
    }
  };

  return (
    <Grid
      gap="small"
      columns={{ count: 3, size: 'xsmall' }}
      margin={{ vertical: 'medium' }}
    >
      {images.map((image) => (
        <div key={image.asset._id}>
          {imageIsActive(image) && (
            <Layer onClickOutside={() => setActiveImageId(null)}>
              <Keyboard
                onRight={() => navigate('next', image)}
                onLeft={() => navigate('prev', image)}
                onEsc={() => setActiveImageId(null)}
                target="document"
              >
                <ImageWithCaption
                  image={image}
                  closeHandler={() => setActiveImageId(null)}
                />
              </Keyboard>
            </Layer>
          )}
          <Box onClick={() => setActiveImageId(image.asset._id)}>
            <Thumbnail image={image} />
          </Box>
        </div>
      ))}
    </Grid>
  );
};

ImageGallery.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      asset: PropTypes.shape({
        _id: PropTypes.String
      })
    })
  )
};

export default ImageGallery;
